// 添加任务类型（从「任务类型」列表的添加按钮进来）：从目标描述创建探索草稿；资料定义与 SOP 从实际运行逐步整理。
// 已建的草稿都在列表里（筛「草稿」），这里只放表单
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorText } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeading } from "../components/PageHeading";
import { CategoryPicker } from "../components/CategoryPicker";

const emptyForm = { name: "", entryUrl: "", category: "other", isQuery: false, description: "" };
function restoreForm(key: string) {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null");
    if (saved && typeof saved.name === "string" && typeof saved.category === "string" && typeof saved.isQuery === "boolean" && typeof saved.description === "string") return { ...emptyForm, ...saved } as typeof emptyForm;
  } catch { /* 存储不可用或旧数据损坏时仍可创建。 */ }
  return emptyForm;
}

export function NewTypePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const storageKey = `csp-new-type-draft:${user?.id ?? "anonymous"}`;
  const [initial] = useState(() => restoreForm(storageKey));
  const [name, setName] = useState(initial.name);
  const [entryUrl, setEntryUrl] = useState(initial.entryUrl);
  const [category, setCategory] = useState(initial.category);
  const [isQuery, setIsQuery] = useState(initial.isQuery);
  const [description, setDescription] = useState(initial.description);
  const [storageError, setStorageError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (done) return;
    try {
      if (name || entryUrl || description || category !== "other" || isQuery) localStorage.setItem(storageKey, JSON.stringify({ name, entryUrl, category, isQuery, description }));
      else localStorage.removeItem(storageKey);
      setStorageError(false);
    } catch { setStorageError(true); }
  }, [storageKey, name, entryUrl, category, isQuery, description, done]);
  const submit = async () => {
    if (!name.trim()) { setError("请填写名称"); return; }
    if (!entryUrl.trim()) { setError("请填写开始网址"); return; }
    if (!/^https?:\/\/\S+$/.test(entryUrl.trim())) { setError("开始网址要以 https:// 开头，例如 https://www.bizfile.gov.sg/"); return; }
    if (!description.trim()) { setError("请描述要做什么"); return; }
    setError(null); setBusy(true);
    try {
      const t = await api.createType({ name: name.trim(), entryUrl: entryUrl.trim(), category, isQuery, description: description.trim(), learningMode: true });
      try { localStorage.removeItem(storageKey); } catch { /* 服务器已保存。 */ }
      setDone(true);
      if (user?.role === "admin") navigate(`/admin/types/${t.id}`);
      else setDone(true);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  return <div>
    <Link to="/admin/types" className="back">← 返回列表</Link>
    <PageHeading title="添加任务类型" desc="填写名称、分类和目标描述，首次探索后再整理 SOP、字段和文件清单；已建的草稿在列表里继续编辑" />
    {done ? <div className="notice ok">类型草稿已建立，等待管理员开始探索。<Link to="/">回任务列表</Link></div> : <>
      <div className="card">
        <h2>基本信息</h2>
        <p className="muted small">未提交的输入自动保留在当前浏览器；点击「建立类型草稿」后保存到服务器。</p>
        {storageError && <p className="notice err">浏览器无法保留输入，请及时建立类型草稿。</p>}
        <div className="form-grid">
          <label className="field">
            <span className="lbl">名称<span className="req">*</span></span>
            <input aria-label="名称" value={name} maxLength={100} onChange={e => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="lbl">开始网址<span className="req">*</span></span>
            <input aria-label="开始网址" type="url" value={entryUrl} maxLength={500} onChange={e => setEntryUrl(e.target.value)} placeholder="开始申请时打开的网址，例如 https://www.bizfile.gov.sg/" />
          </label>
          <label className="field">
            <span className="lbl">分类<span className="req">*</span></span>
            <CategoryPicker value={category} onChange={setCategory} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={isQuery} onChange={e => setIsQuery(e.target.checked)} />
          查询类（只查不提交）
        </label>
        <label className="field">
          <span className="lbl">任务描述<span className="req">*</span></span>
          <textarea aria-label="任务描述" value={description} maxLength={2000} onChange={e => setDescription(e.target.value)} style={{ minHeight: 200 }} placeholder="例如：登录 Bizfile，查看我能代表的公司，选择一家公司查询基本资料。只查询，不提交或付款。" />
        </label>
        <p className="muted small">首次探索时，需要登录会等你扫码；缺少资料会暂停询问。SOP、字段、文件清单根据实际页面逐步整理，你不用提前填写。</p>
        <p className="muted small">保存只创建草稿，不会启动浏览器。由你在下一页点击「开始探索」。</p>
      </div>
      {error && <div className="notice err">{error}</div>}
      <div className="row end"><button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>{busy ? "保存中…" : "建立类型草稿"}</button></div>
    </>}
  </div>;
}
